import { Button, Dropdown, Form, InputField, PageShell, TextAreaField } from 'components';
import React, { useState, useEffect, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch, addInvoice, fetchClients, uploadInvoiceDocs } from 'data';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserDetailsSection } from './UserDetailsSection';
import { formatSwissAmount, getApiErrorMessage, isFutureDateInput, todayDateInputValue, typeOfInvoiceOptions } from 'helpers';
import { PrimeIcons } from 'primereact/api';
import Swal from 'sweetalert2';

export const CreateNewInvoice = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch<AppDispatch>();

	const { addInvoiceLoading } = useSelector(
		(state: RootState) => state.invoices,
	);
	const { clients, getClientsLoading, clientsError } = useSelector(
		(state: RootState) => state.clients,
	);
	const [documentFile, setDocumentFile] = useState<File | null>(null);
	const [submitted, setSubmitted] = useState(false);
	const [startDateSaveError, setStartDateSaveError] = useState<string | null>(null);

	const initialFormData = {
		status: 'pending',
		account: '',
		clientId: 0,
		startDate: '',
		endDate: '',
		assignmentRole: '',
		grossAmount: '',
		currency: 'CHF',
		hours: '',
		timeRate: '',
		paymentTerms: '14',
		shippingDate: '',
		isOwnShipment: false,
		invoiceText: '',
		note: '',
		days: '',
		invoiceType: 'Hourly',
		contactEmail: '',
	};

  	const [formData, setFormData] =
    useState<typeof initialFormData>(initialFormData);
	const [invoiceType, setInvoiceType] = useState<string>('Hourly'); // Set Hourly as default
	const [currentPage] = useState<number>(0);
	const itemsPerPage = 10;

	const calculateInvoiceDetails = () => {
		const hours = parseFloat(formData.hours) || 0;
		const rate = parseFloat(formData.timeRate) || 0;
		const gross = parseFloat(formData.grossAmount) || 0;
		const days = parseFloat(formData.days) || 0;

		switch (invoiceType) {
			case 'Flat Rate':
				return {
				hours: hours.toString(),
				rate: rate.toString(),
				grossAmount: (hours * rate).toFixed(2),
				};
			case 'Monthly': {
				const monthlyHours = parseFloat(formData.hours) || 0;
				const monthlyRate = monthlyHours > 0 ? gross / monthlyHours : 0;
				return {
					hours: formData.hours || (gross > 0 ? (gross / 50).toFixed(2) : ''),
					rate: (monthlyRate > 0 ? monthlyRate : 0).toFixed(2),
					grossAmount: gross.toFixed(2),
				};
			}
			case 'Daily':
				return {
				hours: (days * 8).toFixed(2),
				rate: (rate / 8).toFixed(2),
				grossAmount: (days * rate).toFixed(2),
				};
			case 'Hourly':
			default:
			return {
			hours: hours.toString(),
			rate: rate.toString(),
			grossAmount: (hours * rate).toFixed(2),
			};
		}
	};

	const calculateGrossAmount = () => {
		return calculateInvoiceDetails().grossAmount;
	};

	const calculateVATDetails = () => {
		const grossAmount = parseFloat(calculateGrossAmount()) || 0;
		const selectedClient = clients.find(client => client.id === formData.clientId);
		const isSwissClient = selectedClient?.address?.Country === 'CH';
		
		if (formData.currency === 'CHF' && isSwissClient) {
			const vatRate = 0.081;
			const vat = (grossAmount * vatRate).toFixed(2);
			const amountInclVAT = (grossAmount + parseFloat(vat)).toFixed(2);
			return {
				amountExclVAT: grossAmount.toFixed(2),
				vat,
				amountInclVAT,
			};
		}
		
		// For non-Swiss clients or non-CHF currency, use gross amount as amountInclVAT
		return {
			amountExclVAT: grossAmount.toFixed(2),
			vat: '0.00',
			amountInclVAT: grossAmount.toFixed(2),
		};
	};

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const clientId = searchParams.get('clientId');

		if (clientId && clients.length > 0 && !getClientsLoading) {
			const selectedClient = clients.find(
				(client) => client.id === Number(clientId),
			);
			if (selectedClient) {
				setFormData((prev) => ({
					...prev,
					clientId: selectedClient?.id ?? 0,
					account: selectedClient?.name ?? '',
					contactEmail: selectedClient?.email ?? '',
				}));
			}
		}
	}, [location.search, clients, getClientsLoading]);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const newClient = searchParams.get('newClient');

		const selectedClient = clients.find(
			(client) => client.name === newClient,
		);

		if (newClient && selectedClient) {
			setFormData({
				...initialFormData,
				account: selectedClient.name,
				clientId: selectedClient.id,
				contactEmail: selectedClient.email ?? '',
			});
		}
	}, [location.search, location.state]);

	useEffect(() => {
		dispatch(fetchClients({ page: currentPage, per_page: itemsPerPage }));
	}, [dispatch, currentPage, itemsPerPage]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value, type } = e.target;
		const checked = (e.target as HTMLInputElement).checked;

		if (name === 'startDate') {
			setStartDateSaveError(null);
		}

		if (name === 'invoiceType') {
			setInvoiceType(value);
			setFormData((prev) => ({
				...prev,
				invoiceType: value,
				hours: '',
				timeRate: '',
				grossAmount: '',
				days: '',
			}));
			if (value === 'Flat Rate') {
				setFormData((prev) => ({
					...prev,
					hours: '1',
				}));
			}
			return;
		}

		if (name === 'grossAmount' && invoiceType === 'Monthly') {
			const gross = parseFloat(value) || 0;
			const derivedHours = gross > 0 ? (gross / 50).toFixed(2) : '';
			setFormData((prev) => ({
				...prev,
				grossAmount: value,
				hours: derivedHours,
			}));
			return;
		}

		if (name === 'hours' && invoiceType === 'Monthly') {
			const gross = parseFloat(formData.grossAmount) || 0;
			const maxHours = parseFloat(((gross / 50)).toFixed(2));
			const newHours = parseFloat(value);
			if (value === '' || value === undefined) {
				setFormData((prev) => ({ ...prev, hours: value }));
				return;
			}
			if (!Number.isNaN(newHours) && newHours <= maxHours) {
				setFormData((prev) => ({ ...prev, hours: value }));
			}
			return;
		}

		if (name === 'account' && value !== '') {
			const selectedClient = clients.find((client) => client.name === value);
			if (selectedClient) {
				setFormData((prev) => ({
					...prev,
					account: selectedClient.name,
					clientId: selectedClient.id,
					contactEmail: selectedClient.email ?? '',
				}));
				return;
			}
		}

		// Handle checkbox for own shipment
		if (name === 'isOwnShipment') {
			setFormData((prev) => ({
				...prev,
				isOwnShipment: checked,
				shippingDate: checked ? '' : prev.shippingDate,
			}));
			// Clear document file when own shipment is selected
			if (checked) {
				setDocumentFile(null);
			}
			return;
		}

		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitted(true);
		if (formData.clientId == 0 ||
			formData.startDate == '' ||
			formData.endDate == '' ||
			isFutureDateInput(formData.startDate)) {
			return
		}
		if (invoiceType == 'Hourly') {
			if (!formData.hours || !formData.timeRate || parseFloat(formData.hours) <= 0 || parseFloat(formData.hours) > 180 || parseFloat(formData.timeRate) < 40) {
				return;
			}
		}
		if (invoiceType == 'Daily') {
			if (!formData.days || !formData.timeRate) {
				return;
			}
		}
		if (invoiceType == 'Monthly') {
			if (!formData.grossAmount || !formData.hours) {
				return;
			}
		}
		if (invoiceType == 'Flat Rate') {
			if (!formData.hours || !formData.timeRate || parseFloat(formData.hours) <= 0 || parseFloat(formData.hours) > 180 || parseFloat(formData.timeRate) < 40) {
				return;
			}
		}

		const details = calculateInvoiceDetails();
		const vatDetails = calculateVATDetails();

		const invoicePayload = {
			clientId: formData.clientId,
			clientName: formData.account,
			status: 'pending',
			startDate: formData.startDate,
			endDate: formData.endDate,
			assignmentRole: formData.assignmentRole,
			grossAmount: parseFloat(details.grossAmount),
			days: parseFloat(formData.days) || 0,
			currency: formData.currency,
			hours: parseFloat(details.hours),
			timeRate: parseFloat(details.rate),
			paymentTerms: formData.paymentTerms,
			shippingDate: formData.shippingDate,
			invoiceText: formData.invoiceText,
			note: formData.note,
			amountInclVAT: parseFloat(vatDetails.amountInclVAT),
			invoiceType: formData.invoiceType,
			contactEmail: formData.contactEmail,
			payslipAmount: '',
			payslipNumber: '',
			placementNumber: '',
			invoiceSentDate: '',
			invoiceNumber: ''
		};
		if (invoicePayload.startDate) {
			invoicePayload.startDate = new Date(invoicePayload.startDate).toISOString();
		}
		if (invoicePayload.endDate) {
			invoicePayload.endDate = new Date(invoicePayload.endDate).toISOString();
		}
		if (invoicePayload.shippingDate) {
			invoicePayload.shippingDate = new Date(invoicePayload.shippingDate).toISOString();
		}

		try {
			const resp = await dispatch(addInvoice(invoicePayload)).unwrap();
			await handleDocumentsUpload(resp.id);
			
			// Show success alert
			Swal.fire({
				icon: 'success',
				title: t('createNewInvoice.toast.invoiceCreatedSuccessfully') as string,
				text: t('createNewInvoice.toast.invoiceGenerationMessage') as string,
				confirmButtonText: 'OK',
				confirmButtonColor: '#3085d6'
			});
			navigate('/invoices');
			// });
		} catch (err) {
			if (/start date cannot be in the future/i.test(getApiErrorMessage({ response: { data: err } }))) {
				setStartDateSaveError(t('createNewInvoice.dateInFuture') as string);
			}
			console.error(t('createNewInvoice.toast.invoiceCreationFailed'), err);
		}
	};

  	const handleDocumentsUpload = async (invoiceId: number) => {
		if (!documentFile) {
			console.log('No file to upload');
			return;
		}

		const formData = new FormData();
		formData.append('files', documentFile);

		try {
			const response = await uploadInvoiceDocs(formData, invoiceId);
			console.log('Upload success:', response.data);
		} catch (error) {
			console.error('Upload error:', error);
		}
	};

	const handleCreateNewClient = () => {
		navigate('/clients/add-new', { state: { fromInvoice: formData } });
	};

	const clientOptions = [
		{ label: t('createNewInvoice.chooseClient'), value: '' },
		...(getClientsLoading
		? [{ label: t('createNewInvoice.loadingClients'), value: '' }]
		: clientsError
			? [{ label: t('createNewInvoice.errorLoadingClients'), value: '' }]
				: clients.map((client) => ({
					label: client.name,
					value: client.name,
			}))),
	];

	const getFieldWidthClass = () => {
		switch (invoiceType) {
		case 'Monthly':
			return 'lg:w-1/2';
		case 'Flat Rate':
			return 'lg:w-1/4';
		case 'Daily':
		case 'Hourly':
		default:
			return 'lg:w-1/4';
		}
	};

	const getEndOfMonth = (dateString: string) => {
		if (!dateString) return '';
		const date = new Date(dateString);
		const year = date.getFullYear();
		const month = date.getMonth();
		// Create new date with next month's first day, then subtract 1 day
		const endOfMonth = new Date(year, month + 1, 0);
		// Use local date formatting to avoid UTC conversion issues
		return endOfMonth.toLocaleDateString('en-CA'); // Format as YYYY-MM-DD in local time
	};

  	const handleUploadDocument = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const file = files[0];
			// Validate file type
			const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
			const allowedExtensions = ['.pdf', '.png', '.jpeg', '.jpg'];
			const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
			
			if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
				Swal.fire({
					icon: 'error',
					title: t('createNewInvoice.invalidFileType') || 'Invalid File Type',
					text: t('createNewInvoice.allowedFileTypes') || 'Only PDF, PNG, JPEG, and JPG files are allowed.',
					confirmButtonText: 'OK',
					confirmButtonColor: '#3085d6'
				});
				event.target.value = '';
				return;
			}
			
			setDocumentFile(file);
			console.log('documentFile:', file);
		} else {
			console.log(t('documents.toast.noTokenAvailable'));
		}
		event.target.value = '';
	};

	const deleteDocument = () => {
		setDocumentFile(null);
	};

  return (
    <PageShell width="narrow">
      <Form
        content={
          <form onSubmit={handleSubmit} className="flex flex-col gap-[16px]">
            <div className="text-[20px] font-medium">
              {t('createNewInvoice.newInvoice')}
            </div>

            <div className="flex flex-col gap-[16px]">
              <div className="flex md:flex-row flex-col-reverse gap-[16px]">
                <div className="flex flex-col w-full md:w-[50%] gap-[16px]">
                  <div className="relative">
                    <Dropdown
                      label={t('createNewInvoice.selectClient')}
                      name="account"
                      value={formData.account}
                      options={clientOptions}
                      onChange={handleChange}
                      fullWidth
					  error={submitted && !formData.account ? 'Required': ''}
                    />
                    <button
                      type="button"
                      onClick={handleCreateNewClient}
                      className="mt-[5px] text-sm text-[#FF6F61] hover:underline"
                    >
                      {t('createNewInvoice.createNewClient')}
                    </button>
                  </div>
                  {formData.clientId > 0 && (() => {
                    const selectedClient = clients.find(c => c.id === formData.clientId);
                    return (
                      <>
                        <div className="flex lg:flex-row flex-col gap-[16px]">
                          <InputField
                            label={t('createNewInvoice.contactPersonFirstName')}
                            name="contactPersonFirstName"
                            value={selectedClient?.contactPersonFirstName ?? ''}
                            onChange={handleChange}
                            fullWidth
                            disable
                          />
                          <InputField
                            label={t('createNewInvoice.contactPersonLastName')}
                            name="contactPersonLastName"
                            value={selectedClient?.contactPersonLastName ?? ''}
                            onChange={handleChange}
                            fullWidth
                            disable
                          />
                        </div>
                        <InputField
                          label={t('createNewInvoice.contactEmail')}
                          name="contactEmail"
                          type="email"
                          value={formData.contactEmail}
                          onChange={handleChange}
                          fullWidth
                          disable
                        />
                      </>
                    );
                  })()}
                  <div className="flex lg:flex-row flex-col gap-[16px]">
                    <InputField
                      label={t('createNewInvoice.startDate')}
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      fullWidth={true}
                      max={todayDateInputValue()}
					  error={submitted && !formData.startDate ? 'Required' : isFutureDateInput(formData.startDate) ? t('createNewInvoice.dateInFuture') as string : startDateSaveError ?? ''}
                    />
                    <InputField
                      label={t('createNewInvoice.endDate')}
                      name="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={handleChange}
                      fullWidth={true}
                      min={formData.startDate}
                      max={getEndOfMonth(formData.startDate)}
					  error={submitted && !formData.endDate ? 'Required': ''}
                    />
                  </div>
                </div>
                <div className="w-full md:w-[50%]">
                  <UserDetailsSection />
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-[16px]">
                <div className="w-full md:w-[50%]">
                  <InputField
                    label={t('createNewInvoice.paymentTerms')}
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={handleChange}
                    placeholder={t('createNewInvoice.enterDays')}
                    type="number"
                    fullWidth
                    text={t('createNewInvoice.daysSuffix')}
                  />
                </div>
                <div className="w-full md:w-[50%]">
                  <Dropdown
                    label={t('createNewInvoice.typeOfInvoice')}
                    name="invoiceType"
                    value={formData.invoiceType}
                    options={typeOfInvoiceOptions.map((option) => ({
                      label: t(`${option.labelKey}`),
                      value: option.value,
                    }))}
                    onChange={handleChange}
                    fullWidth
                  />
                </div>
              </div>

              <div className="flex flex-col lg:flex-row space-y-[16px] lg:space-y-0 lg:space-x-2">
                <div className={`flex ${getFieldWidthClass} w-full`}>
                  <InputField
                    label={t('createNewInvoice.assignmentRole')}
                    name="assignmentRole"
                    value={formData.assignmentRole}
                    onChange={handleChange}
                    placeholder={t('createNewInvoice.enterAssignmentRole')}
                    fullWidth
                  />
                </div>

                {(invoiceType === 'Hourly' || invoiceType === 'Flat Rate' || invoiceType === 'Monthly') && (
                  <div className={`flex ${getFieldWidthClass} w-full`}>
                    <InputField
                      label={t('createNewInvoice.hours')}
                      name="hours"
                      value={formData.hours}
                      onChange={handleChange}
                      placeholder={t('createNewInvoice.enterHoursWorked')}
                      type="number"
                      fullWidth
                      min="0.1"
                      max={invoiceType === 'Monthly' ? (formData.grossAmount ? String(parseFloat(formData.grossAmount) / 50) : undefined) : '180'}
                      step="any"
					  error={
						invoiceType === 'Monthly'
							? (submitted && !formData.hours ? 'Required' : (() => {
								const maxHours = parseFloat(((parseFloat(formData.grossAmount) || 0) / 50).toFixed(2));
								return formData.hours && parseFloat(formData.hours) > maxHours ? (t('createNewInvoice.hoursValidationError') as string) : '';
							})())
							: (submitted && !formData.hours 
								? 'Required' 
								: formData.hours && (parseFloat(formData.hours) <= 0 || parseFloat(formData.hours) > 180) 
									? t('createNewInvoice.hoursValidationError') as string
									: '')
					  }
                    />
                  </div>
                )}

				{invoiceType === 'Daily' && (
                  <div className={`flex ${getFieldWidthClass} w-full`}>
                    <InputField
                      label={t('createNewInvoice.days')}
                      name="days"
                      value={formData.days}
                      onChange={handleChange}
                      placeholder={t('createNewInvoice.enterNumberOfDays')}
                      type="number"
					  step="any"
                      fullWidth
					  error={submitted && !formData.days ? 'Required': ''}
                    />
                  </div>
                )}

                {(invoiceType === 'Hourly' || invoiceType === 'Daily' || invoiceType === 'Flat Rate' || invoiceType === 'Monthly') && (
                  <div className={`${getFieldWidthClass} w-full`}>
                    <InputField
                      label={invoiceType === 'Hourly' || invoiceType === 'Flat Rate' ? t('createNewInvoice.timeBasedRate') : invoiceType === 'Monthly' ? t('createNewInvoice.timeBasedRate') : t('createNewInvoice.dailyRate')}
                      name="timeRate"
                      value={
                        invoiceType === 'Monthly'
                          ? String(formatSwissAmount(calculateInvoiceDetails().rate))
                          : (invoiceType === 'Hourly' || invoiceType === 'Daily' || invoiceType === 'Flat Rate') 
                            ? formData.timeRate
                            : calculateInvoiceDetails().rate
                      }
                      onChange={handleChange}
                      placeholder={t('createNewInvoice.enterRate')}
                      dropdown={{
                        name: 'currency',
                        value: formData.currency,
                        onChange: handleChange,
                        options: [
                          { label: t('createNewInvoice.usd'), value: 'USD' },
                          { label: t('createNewInvoice.eur'), value: 'EUR' },
                          { label: t('createNewInvoice.chf'), value: 'CHF' },
                        ],
                      }}
                      fullWidth
                      min="40"
                      disable={invoiceType === 'Monthly'}
					  error={invoiceType === 'Monthly' ? '' : (submitted && !formData.timeRate ? 'Required': '')}
                    />
                    {invoiceType !== 'Monthly' && formData.timeRate && parseFloat(formData.timeRate) < 40 && (
                      <div 
                        className="text-sm text-blue-600 mt-1"
                        dangerouslySetInnerHTML={{ __html: t('createNewInvoice.contactUsForLowerRate') as string }}
                      />
                    )}
                  </div>
                )}

                {invoiceType === 'Monthly' && (
                  <div className={`flex ${getFieldWidthClass} w-full`}>
                    <InputField
                      label={t('createNewInvoice.grossAmount')}
                      name="grossAmount"
                      value={formData.grossAmount}
                      onChange={handleChange}
                      placeholder={t('createNewInvoice.enterGrossAmount')}
                      type="number"
                      fullWidth
					  dropdown={{
                        name: 'currency',
                        value: formData.currency,
                        onChange: handleChange,
                        options: [
                          { label: t('createNewInvoice.usd'), value: 'USD' },
                          { label: t('createNewInvoice.eur'), value: 'EUR' },
                          { label: t('createNewInvoice.chf'), value: 'CHF' },
                        ],
                      }}
					  error={submitted && !formData.grossAmount ? 'Required': ''}
                    />
                  </div>
                )}

                {(invoiceType === 'Hourly' || invoiceType === 'Daily' || invoiceType === 'Flat Rate') && (
                  <div className={`flex ${getFieldWidthClass} w-full`}>
                    <InputField
                      label={t('createNewInvoice.grossAmount')}
                      name="grossAmount"
					  value={String(formatSwissAmount(calculateGrossAmount()))}
                      placeholder={t('createNewInvoice.calculated')}
                      fullWidth
                      disable
                    />
                  </div>
                )}
              </div>

              {/* VAT Calculation Box for CHF */}
              {formData.currency === 'CHF' && formData.clientId > 0 && clients.find(client => client.id === formData.clientId)?.address?.Country === 'CH' ? (
                <div className="border-t border-b border-[#D9D9D9] py-[24px]">
                  <div className="flex justify-between items-center font-[400] text-[16px]">
                    <span>{t('createNewInvoice.amountExclVat')}</span>
                    <span>CHF {formatSwissAmount(calculateVATDetails().amountExclVAT)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2 font-[400] text-[16px]">
                    <span>{t('createNewInvoice.vat81')}</span>
                    <span>CHF {formatSwissAmount(calculateVATDetails().vat)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2 font-[600] text-[20px]">
                    <span>{t('createNewInvoice.amountInclVat')}</span>
                    <span>CHF {formatSwissAmount(calculateVATDetails().amountInclVAT)}</span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center border-t border-b border-[#D9D9D9] py-[24px]">
                  <span className="font-[400] text-[16px]">
                    {t('createNewInvoice.total')}
                  </span>
                  <span className="text-[20px] font-[500]">
                    {formData.currency || 'EUR'} {formatSwissAmount(calculateGrossAmount())}
                  </span>
                </div>
              )}

				<div className="flex flex-col gap-[12px]">
					<div className="flex items-center gap-[8px]">
						<input
							type="checkbox"
							id="isOwnShipment"
							name="isOwnShipment"
							checked={formData.isOwnShipment}
							onChange={handleChange}
							className="w-[18px] h-[18px] cursor-pointer"
						/>
						<label htmlFor="isOwnShipment" className="text-[#4F5057] text-[14px] font-[400] cursor-pointer">
							{t('createNewInvoice.downloadForOwnShipment') || 'Download for own shipment'}
						</label>
					</div>
					{formData.isOwnShipment ? (
						<div
							role="status"
							className="flex items-start gap-[8px] rounded-[5px] border border-[#CC3F3F] bg-[#FF6F611A] px-[16px] py-[12px]"
						>
							<i className="pi pi-info-circle text-[#FF6F61] mt-[2px]" aria-hidden="true" />
							<p className="text-[#4F5057] text-[14px] font-[400]">
								{t('createNewInvoice.ownShipmentInfo')}
							</p>
						</div>
					) : (
						<InputField
							label={t('createNewInvoice.shipmentViaPayflow') || 'Shipment via Payflow to Client'}
							name="shippingDate"
							type="date"
							value={formData.shippingDate}
							onChange={handleChange}
							sentDateTooltip
						/>
					)}
				</div>

              <InputField
                label={t('createNewInvoice.invoiceText')}
                name="invoiceText"
                type="text"
                value={formData.invoiceText}
                onChange={handleChange}
                placeholder={t('createNewInvoice.enterInvoiceText')}
                fullWidth
                maxLength={90}
              />
              {/* <TextAreaField
                label={t('createNewInvoice.note')}
                name="note"
                value={formData.note}
                onChange={handleTextareaChange}
                placeholder={t('createNewInvoice.enterAdditionalNotes')}
                rows={4}
              /> */}

			{!formData.isOwnShipment && (
				<>
					<div>
						<input
							type="file"
							id="document-upload"
							className="hidden"
							onChange={handleUploadDocument}
							accept=".pdf,.png,.jpg,.jpeg"
						/>
						<Button
							label={t('documents.uploadDocument')}
							onClick={() =>
								document.getElementById('document-upload')?.click()
							}
						/>
					</div>

					{documentFile && (
						<div className="flex items-center">
							<span className="flex items-center gap-[8px] mr-2">
								<i className={PrimeIcons.FILE} style={{fontSize: '1.5rem'}} />
								{documentFile.name}
							</span>
							<Button
								label={t('editInvoice.delete')}
								color="#ea0000"
								style={{ padding: '4px 8px', fontSize: '13px' }}
								onClick={deleteDocument}
							/>
						</div>
					)}
				</>
			)}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-[16px] mt-6">
              <Button
                label={t('createNewInvoice.cancel')}
                onClick={() => navigate('/invoices')}
                color="#828E9F"
                fullWidth
              />
              <Button
                label={
                  addInvoiceLoading
                    ? t('createNewInvoice.loading')
                    : t('createNewInvoice.createInvoice')
                }
                type="submit"
                loading={addInvoiceLoading}
                disabled={addInvoiceLoading}
                fullWidth
              />
            </div>
          </form>
        }
      />
    </PageShell>
  );
};
